import React from 'react';
import { useSelector } from 'react-redux';

import { getTranslation } from 'common/react/utils/translations';
import { selectCutoffs } from '../../selector/ladder';
import { useGetContentQuery } from 'common/store/modules/api/content-api';
import PropTypes from 'prop-types';
import { SvgIcon } from 'common/react/components/SvgIcon';

/*
 * Ladder Table Legend
 */
export const LadderTableLegend = ({ compId }) => {
    const { firstCutOff, secondCutOff } = useSelector(selectCutoffs);

    const hasWildcard = !isNaN(parseInt(secondCutOff)) && secondCutOff !== 0;

    const { promo } = useGetContentQuery(
        {
            type: 'PROMO',
            params: {
                limit: 1,
                tagNames: 'ladder-legend-wildcard',
                references: `AFL_COMPETITION:${compId}`
            }
        },
        {
            skip: !hasWildcard,
            selectFromResult: ({ data: promoData }) => ({
                promo: promoData?.content[0]
            })
        }
    );

    return (
        <div className="ladder-predictor-legend">
            <div className="ladder-predictor-legend__left">
                {!isNaN(parseInt(firstCutOff)) && firstCutOff !== 0 ? (
                    <dl className="ladder-predictor-legend__list">
                        <div className="ladder-predictor-legend__item-wrapper">
                            <dt className="ladder-legend__item ladder-legend__item--key">
                                <span className="ladder-legend__item-advances"></span>
                                {getTranslation(`label.ladder.top`)}{' '}
                                {firstCutOff}:
                            </dt>

                            <dd className="ladder-legend__item">
                                {getTranslation(`label.ladder.qualifyTo`)}
                            </dd>
                        </div>

                        {hasWildcard ? (
                            <div className="ladder-predictor-legend__item-wrapper">
                                <dt className="ladder-legend__item ladder-legend__item--key">
                                    <span className="ladder-legend__item-finals-cut-off"></span>
                                    {`${getTranslation(
                                        'label.ladder.wildcard'
                                    )}:`}
                                </dt>
                                <dd className="ladder-legend__item">
                                    {`${PULSE.app.common.numberHelper.ordinalSuffix(
                                        firstCutOff + 1
                                    )} - ${PULSE.app.common.numberHelper.ordinalSuffix(
                                        secondCutOff
                                    )} ${getTranslation(
                                        `label.ladder.legend.wildcardFinals`
                                    )}`}
                                    {promo && promo.links.length ? (
                                        <a
                                            className="ladder-legend__item ladder-legend__item-link"
                                            href={promo.links[0].promoUrl}
                                        >
                                            {promo.links[0].linkText ||
                                                'Read more'}
                                        </a>
                                    ) : null}
                                </dd>
                            </div>
                        ) : null}
                    </dl>
                ) : null}

                <dl className="ladder-predictor-legend__list">
                    {/* played */}
                    <div className="ladder-predictor-legend__item-wrapper">
                        <dt className="ladder-predictor-legend__item ladder-predictor-legend__item--key">
                            {getTranslation(`label.ladder.played.short`)}:
                        </dt>
                        <dd className="ladder-predictor-legend__item">
                            {getTranslation(`label.ladder.legend.played`)}
                        </dd>
                    </div>

                    {/* points */}
                    <div className="ladder-predictor-legend__item-wrapper">
                        <dt className="ladder-predictor-legend__item ladder-predictor-legend__item--key">
                            {getTranslation(`label.ladder.points.short`)}:
                        </dt>

                        <dd className="ladder-predictor-legend__item">
                            {getTranslation(`label.ladder.points`)}
                        </dd>
                    </div>

                    {/* percentage */}
                    <div className="ladder-predictor-legend__item-wrapper">
                        <dt className="ladder-predictor-legend__item ladder-predictor-legend__item--key">
                            %:
                        </dt>

                        <dd className="ladder-predictor-legend__item">
                            {getTranslation(`label.ladder.legend.percentage`)}
                        </dd>
                    </div>

                    {/* won */}
                    <div className="ladder-predictor-legend__item-wrapper">
                        <dt className="ladder-predictor-legend__item ladder-predictor-legend__item--key">
                            {getTranslation(`label.ladder.won.short`)}:
                        </dt>
                        <dd className="ladder-predictor-legend__item">
                            {getTranslation(`label.ladder.won`)}
                        </dd>
                    </div>

                    {/* lost */}
                    <div className="ladder-predictor-legend__item-wrapper">
                        <dt className="ladder-predictor-legend__item ladder-predictor-legend__item--key">
                            {getTranslation(`label.ladder.lost.short`)}:
                        </dt>

                        <dd className="ladder-predictor-legend__item">
                            {getTranslation(`label.ladder.lost`)}
                        </dd>
                    </div>

                    {/* drawn */}
                    <div className="ladder-predictor-legend__item-wrapper">
                        <dt className="ladder-predictor-legend__item ladder-predictor-legend__item--key">
                            {getTranslation(`label.ladder.drawn.short`)}:
                        </dt>
                        <dd className="ladder-predictor-legend__item">
                            {getTranslation(`label.ladder.drawn`)}
                        </dd>
                    </div>

                    {/* last five (form) */}
                    <div className="ladder-predictor-legend__item-wrapper">
                        <dt className="ladder-predictor-legend__item ladder-predictor-legend__item--key">
                            {getTranslation(`label.form`)}:
                        </dt>
                        <dd className="ladder-predictor-legend__item">
                            {getTranslation(`label.ladder.lastFiveGames`)}
                        </dd>
                    </div>
                </dl>
            </div>
            <dl className="ladder-predictor-legend__right">
                <div className="ladder-legend__item-wrapper">
                    <dt className="ladder-legend__item ladder-legend__item--key">
                        <SvgIcon icon="ladder-form-icon-w" className="icon" />
                    </dt>
                    <dd className="ladder-legend__item">
                        {getTranslation(`label.ladder.won`)}
                    </dd>
                </div>
                <div className="ladder-legend__item-wrapper">
                    <dt className="ladder-legend__item ladder-legend__item--key">
                        <SvgIcon icon="ladder-form-icon-l" className="icon" />
                    </dt>
                    <dd className="ladder-legend__item">
                        {getTranslation(`label.ladder.lost`)}
                    </dd>
                </div>
                <div className="ladder-legend__item-wrapper">
                    <dt className="ladder-legend__item ladder-legend__item--key">
                        <SvgIcon icon="ladder-form-icon-d" className="icon" />
                    </dt>
                    <dd className="ladder-legend__item">
                        {getTranslation(`label.ladder.drawn`)}
                    </dd>
                </div>
                <div className="ladder-legend__item-wrapper">
                    <dt className="ladder-legend__item ladder-legend__item--key">
                        <SvgIcon icon="ladder-form-icon-b" className="icon" />
                    </dt>
                    <dd className="ladder-legend__item">
                        {getTranslation(`label.ladder.bye`)}
                    </dd>
                </div>
                <div className="ladder-legend__item-wrapper">
                    <dt className="ladder-legend__item ladder-legend__item--key">
                        <span className="ladder-legend__item-not-played">
                            -
                        </span>
                    </dt>
                    <dd className="ladder-legend__item">
                        {getTranslation(`label.ladder.notPlayed`)}
                    </dd>
                </div>
            </dl>
        </div>
    );
};

LadderTableLegend.propTypes = {
    compId: PropTypes.string.isRequired
};
